import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import * as XLSX from "xlsx-js-style";
import SharedButton from "../../components/Shared/SharedButton";
import AutoCompleteField from "../../components/Shared/AutoCompleteField";
import DatePicker from "../../components/Shared/DatePicker";
import useFetchData from "../../hooks/useFetchData";
import { getOneMonthAgo } from "../CashInflow/CashInflow";
import TrialBalanceTable from "./TrialBalanceTable";
import DropdownComponent from "../../components/Shared/DropdownComponent";
import { getCostCenter } from "../../store/slices/CostCenterSlice";

const getToday = () =>
  new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];

const toDateString = (date) =>
  date
    ? new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .split("T")[0]
    : "";

const formatAccountOptions = (data, lang) =>
  data
    ? Object.entries(data).map(([, account]) => ({
      value: String(account?.code || ""),
      label:
        lang === "ar"
          ? `${account.name_ar} ${account?.code}`
          : `${account.name_en} ${account?.code}`,
    }))
    : [];

const getTrialBalanceValues = (row) => {
  const openingDebit = Number(row?.opening_debit ?? 0);
  const openingCredit = Number(row?.opening_credit ?? 0);
  const oldBalanceDebit = Number(row?.old_debit ?? row?.total_debit ?? 0);
  const oldBalanceCredit = Number(row?.old_credit ?? row?.total_credit ?? 0);
  const movementDebit = Number(row?.month_debit ?? 0);
  const movementCredit = Number(row?.month_credit ?? 0);
  const debitNature = ["Asset", "Expense"];
  const creditNature = ["Liability", "Equity", "Revenue"];
  let closingDebit = 0;
  let closingCredit = 0;

  if (debitNature.includes(row?.account_type)) {
    const net =
      openingDebit +
      oldBalanceDebit +
      movementDebit -
      (openingCredit + oldBalanceCredit + movementCredit);
    closingDebit = net >= 0 ? net : 0;
    closingCredit = net < 0 ? Math.abs(net) : 0;
  } else if (creditNature.includes(row?.account_type)) {
    const net =
      openingCredit +
      oldBalanceCredit +
      movementCredit -
      (openingDebit + oldBalanceDebit + movementDebit);
    closingCredit = net >= 0 ? net : 0;
    closingDebit = net < 0 ? Math.abs(net) : 0;
  }

  return {
    openingDebit,
    openingCredit,
    oldBalanceDebit,
    oldBalanceCredit,
    movementDebit,
    movementCredit,
    closingDebit,
    closingCredit,
  };
};

const dedupeTrialBalanceTree = (items = []) => {
  const seen = new Set();

  return items.reduce((acc, item) => {
    const key = [
      item?.account_id ?? "",
      item?.account_code ?? "",
      item?.parent_code ?? "",
    ].join(":");

    if (seen.has(key)) {
      return acc;
    }

    seen.add(key);
    acc.push({
      ...item,
      children: Array.isArray(item?.children)
        ? dedupeTrialBalanceTree(item.children)
        : [],
    });

    return acc;
  }, []);
};

export default function JournalLedgerTrialBalance({
  reportName = "TrialBalance",
  reportTitle = "Trial Balance Report",
  endpoint = "reports/journalLedgerTrialBalance",
}) {
  const { t } = useTranslation();
  const lang = i18next.language;
  const { user } = useSelector((state) => state.auth);
  const { costCenters } = useSelector((state) => state.costCenter);
  const dispatch = useDispatch();
  const today = getToday();
  const defaultStartDate = getOneMonthAgo(today);
  const iframeRef = useRef(null);
  const [fromAccount, setFromAccount] = useState(null);
  const [toAccount, setToAccount] = useState(null);
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(today);
  const [costCenter, setCostCenter] = useState(null);
  const { data: trialResponse, loading, executeFetch } = useFetchData();

  useEffect(() => {
    dispatch(getCostCenter());
  }, [dispatch]);

  const costCenterOptions = costCenters?.data
    ? Object.entries(costCenters.data).map(([, center]) => ({
        value: Number(center?.id),
        label: lang === "ar" ? center?.name_ar : center?.name_en,
      }))
    : [];

  const rawRows = Array.isArray(trialResponse)
    ? trialResponse
    : trialResponse?.data || [];
  const rows = dedupeTrialBalanceTree(rawRows);

  const flattenTrialBalanceTree = (items = [], level = 0) => {
    let result = [];

    items.forEach((item) => {
      const children = Array.isArray(item?.children) ? item.children : [];
      result.push({ ...item, level, children });

      if (children.length > 0) {
        result = result.concat(flattenTrialBalanceTree(children, level + 1));
      }
    });

    return result;
  };

  const flatRows = flattenTrialBalanceTree(rows);
  const totals = flatRows.reduce(
    (acc, row) => {
      if ((row?.level || 0) !== 0) return acc;
      const values = getTrialBalanceValues(row);

      Object.keys(acc).forEach((key) => {
        acc[key] += values[key];
      });

      return acc;
    },
    {
      openingDebit: 0,
      openingCredit: 0,
      oldBalanceDebit: 0,
      oldBalanceCredit: 0,
      movementDebit: 0,
      movementCredit: 0,
      closingDebit: 0,
      closingCredit: 0,
    },
  );

  const fetchTrialBalance = () => {
    executeFetch("POST", endpoint, {
      start_date: startDate || "",
      end_date: endDate || "",
      from_account: fromAccount?.value ? String(fromAccount.value) : "",
      to_account: toAccount?.value ? String(toAccount.value) : "",
      cost_center_id: costCenter?.value ? String(costCenter.value) : "",
    });
  };

  const reset = () => {
    setFromAccount(null);
    setToAccount(null);
    setStartDate(defaultStartDate);
    setEndDate(today);
    setCostCenter(null);
    executeFetch("POST", endpoint, {
      start_date: defaultStartDate,
      end_date: today,
      from_account: "",
      to_account: "",
      cost_center_id: "",
    });
  };

  const handleExcelExport = () => {
    if (!flatRows.length) {
      toast.error(t("no_results_found"));
      return;
    }

    const businessName =
      lang === "ar"
        ? user?.Business?.name_ar || user?.Business?.name_en
        : user?.Business?.name_en || user?.Business?.name_ar;

    const dataRows = flatRows.map((row, index) => {
      const values = getTrialBalanceValues(row);
      return [
        index + 1,
        row?.account_code || "",
        `${"    ".repeat(row?.level || 0)}${row?.account_name || ""}`,
        t(row?.account_type || ""),
        values.openingDebit,
        values.openingCredit,
        values.oldBalanceDebit,
        values.oldBalanceCredit,
        values.movementDebit,
        values.movementCredit,
        values.closingDebit,
        values.closingCredit,
      ];
    });

    const wsData = [
      [businessName || ""],
      [t(reportTitle)],
      [
        `${t("From")}: ${moment(startDate).format("DD-MM-YYYY")}   ${t("To")}: ${moment(endDate).format("DD-MM-YYYY")}`,
      ],
      [],
      [
        t("no"),
        t("Account Code"),
        t("Account Name"),
        t("Type"),
        t("Opening"),
        "",
        t("Old Balance"),
        "",
        t("Movement"),
        "",
        t("Closing"),
        "",
      ],
      [
        "",
        "",
        "",
        "",
        t("Debit"),
        t("Credit"),
        t("Debit"),
        t("Credit"),
        t("Debit"),
        t("Credit"),
        t("Debit"),
        t("Credit"),
      ],
      ...dataRows,
      [
        t("Total"),
        "",
        "",
        "",
        totals.openingDebit,
        totals.openingCredit,
        totals.oldBalanceDebit,
        totals.oldBalanceCredit,
        totals.movementDebit,
        totals.movementCredit,
        totals.closingDebit,
        totals.closingCredit,
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(wsData);
    worksheet["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 11 } },
      { s: { r: 1, c: 0 }, e: { r: 1, c: 11 } },
      { s: { r: 2, c: 0 }, e: { r: 2, c: 11 } },
      { s: { r: 4, c: 0 }, e: { r: 5, c: 0 } },
      { s: { r: 4, c: 1 }, e: { r: 5, c: 1 } },
      { s: { r: 4, c: 2 }, e: { r: 5, c: 2 } },
      { s: { r: 4, c: 3 }, e: { r: 5, c: 3 } },
      { s: { r: 4, c: 4 }, e: { r: 4, c: 5 } },
      { s: { r: 4, c: 6 }, e: { r: 4, c: 7 } },
      { s: { r: 4, c: 8 }, e: { r: 4, c: 9 } },
      { s: { r: 4, c: 10 }, e: { r: 4, c: 11 } },
      { s: { r: wsData.length - 1, c: 0 }, e: { r: wsData.length - 1, c: 3 } },
    ];
    worksheet["!cols"] = [
      { wch: 5 },
      { wch: 15 },
      { wch: 40 },
      { wch: 15 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
      { wch: 12 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t(reportName));
    XLSX.writeFile(workbook, `${reportName.replace(/\s+/g, "_")}.xlsx`);
  };

  const handlePrint = () => {
    if (!flatRows.length) {
      toast.error(t("no_results_found"));
      return;
    }

    const iframe = iframeRef.current;
    const doc = iframe.contentWindow.document;
    const businessName =
      lang === "ar"
        ? user?.Business?.name_ar || user?.Business?.name_en
        : user?.Business?.name_en || user?.Business?.name_ar;
    const businessLogo = user?.Business?.logo || user?.Business?.logo_url;
    const direction = lang === "ar" ? "rtl" : "ltr";

    const rowsHtml = flatRows
      .map((row, index) => {
        const values = getTrialBalanceValues(row);
        const isParent = row?.children?.length > 0;
        return `
          <tr style="${isParent ? "background:#e5e7eb;font-weight:600;" : ""}">
            <td>${index + 1}</td>
            <td>${row?.account_code || ""}</td>
            <td style="padding-left:${(row?.level || 0) * 18 + 8}px">${row?.account_name || ""}</td>
            <td>${t(row?.account_type || "")}</td>
            <td>${values.openingDebit.toFixed(2)}</td>
            <td>${values.openingCredit.toFixed(2)}</td>
            <td>${values.oldBalanceDebit.toFixed(2)}</td>
            <td>${values.oldBalanceCredit.toFixed(2)}</td>
            <td>${values.movementDebit.toFixed(2)}</td>
            <td>${values.movementCredit.toFixed(2)}</td>
            <td>${values.closingDebit.toFixed(2)}</td>
            <td>${values.closingCredit.toFixed(2)}</td>
          </tr>
        `;
      })
      .join("");

    doc.open();
    doc.write(`
      <html>
        <head>
          <title>${t(reportTitle)}</title>
          <style>
            @media print { @page { size: A4 landscape; margin: 15mm; } }
            body { font-family: sans-serif; direction: ${direction}; color: #111827; }
            .header { text-align: center; margin-bottom: 18px; }
            .header img { height: 70px; object-fit: contain; margin-bottom: 8px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #d1d5db; padding: 7px; }
            th { background: #d1d5db; }
            td:nth-child(n+5) { text-align: right; }
            tfoot td { background: #f3f4f6; font-weight: 700; }
          </style>
        </head>
        <body>
          <div class="header">
            ${businessLogo ? `<img src="${businessLogo}" />` : ""}
            <h2>${businessName || ""}</h2>
            <h3>${t(reportTitle)}</h3>
            <div>${t("From")}: ${moment(startDate).format("DD-MM-YYYY")} - ${t("To")}: ${moment(endDate).format("DD-MM-YYYY")}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th rowspan="2">${t("no")}</th>
                <th rowspan="2">${t("Account Code")}</th>
                <th rowspan="2">${t("Account Name")}</th>
                <th rowspan="2">${t("Type")}</th>
                <th colspan="2">${t("Opening")}</th>
                <th colspan="2">${t("Old Balance")}</th>
                <th colspan="2">${t("Movement")}</th>
                <th colspan="2">${t("Closing")}</th>
              </tr>
              <tr>
                <th>${t("Debit")}</th>
                <th>${t("Credit")}</th>
                <th>${t("Debit")}</th>
                <th>${t("Credit")}</th>
                <th>${t("Debit")}</th>
                <th>${t("Credit")}</th>
                <th>${t("Debit")}</th>
                <th>${t("Credit")}</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
            <tfoot>
              <tr>
                <td colspan="4">${t("Total")}</td>
                <td>${totals.openingDebit.toFixed(2)}</td>
                <td>${totals.openingCredit.toFixed(2)}</td>
                <td>${totals.oldBalanceDebit.toFixed(2)}</td>
                <td>${totals.oldBalanceCredit.toFixed(2)}</td>
                <td>${totals.movementDebit.toFixed(2)}</td>
                <td>${totals.movementCredit.toFixed(2)}</td>
                <td>${totals.closingDebit.toFixed(2)}</td>
                <td>${totals.closingCredit.toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </body>
      </html>
    `);
    doc.close();
    iframe.onload = () => iframe.contentWindow.print();
  };

  return (
    <div>
      <div className="flex flex-wrap items-start justify-end gap-2 mb-4">
        <div className="w-full sm:w-[210px] lg:w-[230px]">
          <AutoCompleteField
            useFormik={false}
            placeholder={t("From Account")}
            value={fromAccount}
            onChange={(e) => setFromAccount(e.value)}
            fieldUrl="searchAllAccounts"
            optionsFormat={(data) => formatAccountOptions(data, lang)}
          />
        </div>
        <div className="w-full sm:w-[210px] lg:w-[230px]">
          <AutoCompleteField
            useFormik={false}
            placeholder={t("To Account")}
            value={toAccount}
            onChange={(e) => setToAccount(e.value)}
            fieldUrl="searchAllAccounts"
            optionsFormat={(data) => formatAccountOptions(data, lang)}
          />
        </div>
        <div className="w-full sm:w-[150px] lg:w-[160px]">
          <DatePicker
            useFormik={false}
            value={startDate ? new Date(startDate) : null}
            onChange={(e) => setStartDate(toDateString(e.value))}
          />
        </div>
        <div className="w-full sm:w-[150px] lg:w-[160px]">
          <DatePicker
            useFormik={false}
            value={endDate ? new Date(endDate) : null}
            onChange={(e) => setEndDate(toDateString(e.value))}
          />
        </div>
        <div className="w-full sm:w-[210px] lg:w-[230px]">
          <DropdownComponent
            useFormik={false}
            value={costCenter}
            handleChange={(e) => setCostCenter(e.value)}
            options={costCenterOptions}
            placeholder={t("costCenter")}
            filter
            showClear
          />
        </div>
        <div className="flex items-center justify-end gap-2">
          <SharedButton
            title={t("apply_filter")}
            icon="pi pi-filter"
            onClick={fetchTrialBalance}
            loading={loading}
            disabled={loading}
          />
          <SharedButton
            title={t("Reset")}
            buttonType="reset"
            icon="pi pi-sync"
            onClick={reset}
            disabled={loading}
          />
          <SharedButton
            title={t("Export Excel")}
            icon="pi pi-file-excel"
            iconSize={20}
            onClick={handleExcelExport}
            disabled={loading}
          />
          <SharedButton
            title={t("Export PDF")}
            icon="pi pi-print"
            iconSize={20}
            onClick={handlePrint}
            disabled={loading}
          />
        </div>
      </div>
      <TrialBalanceTable data={{ data: flatRows }} isLoading={loading} />
      <iframe
        ref={iframeRef}
        title="trial-balance-print-frame"
        style={{ display: "none" }}
      ></iframe>
    </div>
  );
}
